<template>
  <Container class="tw-mt-12">
    <div>
      <h1 class="tw-heading-2">Session Request</h1>
    </div>
    <div
      class="tw-mt-4 tw-flex tw-flex-col tw-space-y-4 lg:tw-flex-row lg:tw-space-x-8 lg:tw-space-y-0"
    >
      <div class="tw-flex tw-w-full tw-flex-col tw-space-y-4 lg:tw-w-9/12">
        <Card>
          <h2 class="tw-heading-3">Session Details</h2>
          <div class="tw-my-6 tw-flex tw-items-center tw-space-x-3">
            <InertiaLink :href="`/mentors/${mentor.slug}`">
              <Avatar
                :src="mentor.avatarUrl"
                :alt="`${mentor.displayName}'s avatar`"
                :is-featured="mentor.featured"
                size="lg"
              />
            </InertiaLink>
            <div>
              <h3 class="tw-text-neutral-400">Mentor</h3>
              <InertiaLink :href="`/mentors/${mentor.slug}`">
                <h4 class="tw-font-semibold">{{ mentor.displayName }}</h4>
              </InertiaLink>
            </div>
          </div>
          <hr />
          <div class="tw-mt-6">
            <h3 class="tw-heading-5">Schedule Session Duration</h3>
            <p class="tw-text-sm tw-text-neutral-500">
              Sessions must be scheduled at least 48 hours in advance.
            </p>
            <div class="tw-mt-4">
              <FormRadioGroup
                v-model="sessionRequestForm.duration"
                :options="sessionDurationOptions"
                button-classes="tw-p-5 tw-space-x-5"
              >
                <template #default="{ option }">
                  <div class="tw-text-left">
                    <div class="tw-text-xl tw-font-semibold">
                      {{ option.value }} min
                    </div>
                    <div class="tw-text-sm tw-text-neutral-400">
                      {{ option.cost }} total bill
                    </div>
                  </div>
                </template>
              </FormRadioGroup>
            </div>
          </div>
          <div class="tw-mt-6">
            <h3 class="tw-heading-5">Communication Tool</h3>
            <p class="tw-text-sm tw-text-neutral-500">
              Choose which tool you wish to use to have your session.
            </p>
            <div class="tw-mt-4">
              <FormRadioGroup
                v-model="sessionRequestForm.communicationTool"
                :errors="sessionRequestForm.errors.communication_tool"
                @input="sessionRequestForm.clearErrors('communication_tool')"
                :options="mentor.communicationToolOptions"
                button-classes="tw-h-20 tw-space-x-5"
                @click="handleInputClick"
              >
                <template #default="{ option }">
                  <div
                    class="tw-mr-4 tw-flex tw-items-center tw-space-x-4 tw-text-left"
                  >
                    <SkypeLogo
                      v-if="option.value === 'skype'"
                      class="tw-w-8 tw-shrink-0"
                    />
                    <ZoomLogo
                      v-if="option.value === 'zoom'"
                      class="tw-w-8 tw-shrink-0"
                    />
                    <GoogleMeetLogo
                      v-if="option.value === 'google_hangouts'"
                      class="tw-w-10 tw-shrink-0"
                    />
                    <img
                      v-if="option.value === 'growthmentor'"
                      src="../../../assets/images/growthmentor-call-badge.png"
                      alt=""
                      class="tw-w-24"
                    />
                    <div>
                      <div>
                        {{ option.label }}
                      </div>
                      <div
                        v-if="option.value === 'growthmentor'"
                        class="tw-text-sm tw-text-neutral-500"
                      >
                        Now you can start a video call right from your session
                      </div>
                    </div>
                  </div>
                </template>
              </FormRadioGroup>
            </div>
          </div>
        </Card>
        <Card>
          <div>
            <h3 class="tw-heading-5">Schedule Session</h3>
            <p class="tw-text-sm tw-text-neutral-500">
              Sessions must be scheduled at least 48 hours in advance.
            </p>
            <BaseButton
              @click="openModal(CALENDAR_SLOT_PICKER_MODAL)"
              class="tw-mt-4"
            >
              <OpeningTimesIcon class="tw-mr-2 tw-h-4 tw-w-4" />
              {{
                sessionRequestForm.startsAt
                  ? formatDate(sessionRequestForm.startsAt)
                  : 'Select Date/Time'
              }}
            </BaseButton>
            <FormErrors
              :errors="sessionRequestForm.errors.starts_at"
              class="tw-mt-2"
            />
          </div>
          <hr class="tw-my-6" />
          <div>
            <FormInput
              v-model="sessionRequestForm.headline"
              :is-error="Boolean(sessionRequestForm.errors.headline)"
              :errors="sessionRequestForm.errors.headline"
              @input="sessionRequestForm.clearErrors('headline')"
              tooltip="Summarize the topic of your request in just 1 sentence."
              type="text"
              @click="handleInputClick"
            >
              <template #label>Objective</template>
            </FormInput>
          </div>
          <hr class="tw-my-6" />
          <div>
            <FormSelect
              v-model="sessionRequestForm.category"
              :is-error="Boolean(sessionRequestForm.errors.category)"
              :errors="sessionRequestForm.errors.category"
              @input="sessionRequestForm.clearErrors('category')"
              :options="mentor.expertiseList"
              tooltip=""
              test_id="session-category-select"
            >
              <template #label>
                Functional skills area in which you are seeking advice
              </template>
            </FormSelect>
            <FormInput
              v-model="sessionRequestForm.message"
              :is-error="Boolean(sessionRequestForm.errors.message)"
              :errors="sessionRequestForm.errors.message"
              @input="sessionRequestForm.clearErrors('message')"
              @click="handleInputClick"
              as="textarea"
              class="tw-my-6"
              rows="4"
              test_id="session-message"
              tooltip="Write a couple of sentences describing the core challenge you're facing."
              type="text"
              :min-characters="100"
            >
              <template #label>Problem/challenge</template>
            </FormInput>
            <FormInput
              v-model="sessionRequestForm.desirableOutcome"
              :is-error="Boolean(sessionRequestForm.errors.desirable_outcome)"
              :errors="sessionRequestForm.errors.desirable_outcome"
              @input="sessionRequestForm.clearErrors('desirable_outcome')"
              @click="handleInputClick"
              as="textarea"
              tooltip="Paint a picture of what a successful session would look like to you."
              class="tw-my-6"
              rows="4"
              test_id="session-desirable-outcome"
              :min-characters="100"
            >
              <template #label>Desired outcome of the call</template>
            </FormInput>
          </div>

          <div v-if="mentor.pricing > 0" class="tw-mt-4">
            <h2 class="tw-heading-3">Payment Details</h2>
            <div class="tw-my-3">
              <FormRadio
                v-model="sessionRequestForm.creditCard"
                v-for="card in cards"
                :key="card.id"
                :value="card.stripeId"
                class="tw-mb-2"
              >
                <div v-if="card.customerType === 'User'">
                  <span class="tw-capitalize">{{ card.provider }}</span>
                  ending {{ card.displayText }}
                </div>
                <div v-else>Team card ({{ card.team }})</div>
              </FormRadio>
              <FormErrors
                :errors="sessionRequestForm.errors.creditCard"
                class="tw-mt-2"
              />
            </div>
            <BaseButton
              size="small"
              @click="addNewCard"
              test_id="add-new-card-button"
            >
              Add new card
            </BaseButton>
            <hr class="tw-my-4" />
          </div>
          <FormCheckbox
            v-model="rulesCheckbox.status"
            :is-error="rulesCheckbox.isError"
            @input="rulesCheckbox.isError = false"
            test_id="form-checkbox"
          >
            <p>
              I have properly formulated this session request as per
              <a
                href="https://www.growthmentor.com/faq/how-to-write-a-session-request/"
                class="tw-link"
                target="_blank"
              >
                best practices
              </a>
              and will ensure that I show up in a timely fashion to the call.
            </p>
          </FormCheckbox>
          <div class="tw-mt-4">
            <BaseButton
              v-if="mentor.id !== currentUser.id"
              color="primary"
              :is-disabled="sessionRequestForm.processing"
              @click="submitSessionRequest"
              test_id="confirm-session-button"
            >
              <LoadingCircle
                v-if="sessionRequestForm.processing"
                size="16"
                color="tw-text-white"
                class="tw-mr-2"
              />
              Confirm Session Request
            </BaseButton>
            <div
              v-else
              v-tooltip="'You cannot create a session with yourself'"
              class="tw-w-max"
            >
              <BaseButton color="primary" is-disabled>
                Confirm Session Request
              </BaseButton>
            </div>
          </div>
        </Card>
      </div>
      <div class="tw-w-full lg:tw-w-3/12">
        <DashboardCarousel :elements="videos" header="Tips & Resources">
          <template #default="{ element }">
            <TipsAndResourcesVideo
              :id="element.id"
              :title="element.title"
              :wistia-id="element.wistiaId"
              :youtube-id="element.youtubeId"
              :tracking-object="{
                name: 'New Session Video Clicked',
              }"
            />
          </template>
        </DashboardCarousel>
      </div>
    </div>

    <NewCreditCardModal
      :name="NEW_CREDIT_CARD_MODAL"
      :stripe-key="stripePublicKey"
      @success="reloadCards"
    />
    <CalendarSlotPickerModal
      v-model="sessionRequestForm.startsAt"
      :name="CALENDAR_SLOT_PICKER_MODAL"
      :selected-session-duration="Number(sessionRequestForm.duration)"
      @input="sessionRequestForm.clearErrors('starts_at')"
    />
  </Container>
  <UnlockFullAccessModal v-if="hasRole(ROLE.GUEST)" />
</template>

<script setup>
import Container from '@/shared/components/Container.vue'
import Card from '@/shared/components/Card.vue'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import FormCheckbox from '@/shared/components/forms/FormCheckbox.vue'
import FormRadio from '@/shared/components/forms/FormRadio.vue'
import FormRadioGroup from '@/shared/components/forms/FormRadioGroup.vue'
import SkypeLogo from '@/assets/images/skype.svg'
import ZoomLogo from '@/assets/images/zoom.svg'
import GoogleMeetLogo from '@/assets/images/google-meet.svg'
import NewCreditCardModal from '@/shared/components/modals/NewCreditCardModal.vue'
import FormSelect from '@/shared/components/forms/FormSelect.vue'
import { router, useForm } from '@inertiajs/vue3'
import snakeCaseKeys from 'snakecase-keys'
import FormErrors from '@/shared/components/forms/FormErrors.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import OpeningTimesIcon from '@/assets/images/icons/opening-times.svg'
import TipsAndResourcesVideo from '@/shared/components/TipsAndResourcesVideo.vue'
import CalendarSlotPickerModal from '@/shared/components/calendar/CalendarSlotPickerModal.vue'
import { computed, ref, watch } from 'vue'
import { useModals } from '@/shared/composables/useModals.js'
import { useDateManager } from '@/shared/composables/useDateManager.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser.js'
import DashboardCarousel from '@/modules/growthmentor/dashboard/components/DashboardCarousel.vue'
import UnlockFullAccessModal from '@/shared/components/modals/UnlockFullAccessModal.vue'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { ROLE } from '@/shared/data/permissionSystem.js'

const props = defineProps({
  mentor: {
    type: Object,
    default: () => {},
  },
  videos: {
    type: Array,
    default: () => [],
  },
  cards: {
    type: Array,
    default: () => [],
  },
  stripePublicKey: {
    type: String,
    default: '',
  },
})

const { openModal } = useModals()
const { formatDate } = useDateManager()
const { currentUser } = useCurrentUser()
const { hasRole } = usePermissionSystem()

const NEW_CREDIT_CARD_MODAL = 'new-credit-card'
const CALENDAR_SLOT_PICKER_MODAL = 'calendar-slot-picker'
const rulesCheckbox = ref({
  status: false,
  isError: false,
})
const sessionRequestForm = useForm({
  duration: String(props.mentor.initialCourseDuration),
  communicationTool: null,
  startsAt: null,
  headline: '',
  category: '',
  message: '',
  desirableOutcome: '',
  creditCard: props.cards.length
    ? props.cards.find((card) => card.default).stripeId
    : null,
})

const sessionDurationOptions = computed(() => {
  return Object.entries(props.mentor.courseDurations).map(([key, value]) => ({
    value: key,
    cost: value,
  }))
})

watch(
  () => sessionRequestForm.duration,
  () => {
    sessionRequestForm.startsAt = null
  },
)

const reloadCards = () => {
  router.reload({ only: ['cards'] })
}

const submitSessionRequest = () => {
  if (hasRole(ROLE.GUEST)) {
    return openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  }

  if (!rulesCheckbox.value.status) {
    rulesCheckbox.value.isError = true
    return
  }

  sessionRequestForm
    .transform((data) => {
      return {
        course: {
          ...snakeCaseKeys(data),
        },
      }
    })
    .post(`/mentors/${props.mentor.slug}/sessions`)
}

const handleInputClick = () => {
  if (hasRole(ROLE.GUEST)) {
    openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  }
}

const addNewCard = () => {
  if (hasRole(ROLE.GUEST)) {
    openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  } else {
    openModal(NEW_CREDIT_CARD_MODAL)
  }
}
</script>
